import { LitElement, html, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { RmaError } from 'components/ordering/rma/api/dtos/error.js'
import { uploadRmaLineFileContext, uploadTemporaryRmaLineFileContext } from 'components/ordering/rma/components/context/rma-context.js'
import { RMA_TEMPORARY_FILE_UPLOADED } from 'components/ordering/rma/events.js'
import { fileUpload } from './file-upload.style.js'

const translations = {
  label: () => t('label', { ns: 'csc-rma-file-upload', default: 'Photo of the product/issue' }),
  optional: () => t('optional', { ns: 'csc-rma-file-upload', default: 'Optional' }),
  choose: () => t('choose', { ns: 'csc-rma-file-upload', default: 'Choose file' }),
  noFile: () => t('no_file', { ns: 'csc-rma-file-upload', default: 'No file selected' }),
  uploading: () => t('uploading', { ns: 'csc-rma-file-upload', default: 'Uploading…' }),
  error: () => t('error', { ns: 'csc-rma-file-upload', default: 'Uploading the file failed. Please try again.' }),
}

@customElement('csc-rma-file-upload')
export class CSCRmaFileUpload extends LitElement {
  static override styles = [hostStyle, fileUpload]

  @property({ attribute: 'line-id', type: String })
  public lineId = ''

  @property({ type: Boolean })
  public required = false

  @query('input[type="file"]')
  private fileInput!: HTMLInputElement

  @consume({ context: uploadRmaLineFileContext, subscribe: true })
  private uploadFile: ((lineId: string, file: File) => Promise<void>) | null = null

  @consume({ context: uploadTemporaryRmaLineFileContext, subscribe: true })
  private uploadTemporaryFile: ((file: File) => Promise<string>) | null = null

  @state() private uploading = false
  @state() private error: string | ReturnType<typeof t> | null = null
  @state() private fileName = ''

  private get isDisabled(): boolean {
    return this.uploading
  }

  private openPicker() {
    if (this.isDisabled) return
    this.fileInput?.click()
  }

  private async onFileChange() {
    const file = this.fileInput?.files?.[0]
    if (!file) return
    this.fileName = file.name
    this.uploading = true
    this.error = null
    try {
      if (this.lineId) {
        if (!this.uploadFile) return
        await this.uploadFile(this.lineId, file)
      } else {
        if (!this.uploadTemporaryFile) return
        const identifier = await this.uploadTemporaryFile(file)
        this.dispatchEvent(new CustomEvent(RMA_TEMPORARY_FILE_UPLOADED, {
          detail: { identifier, name: file.name },
          bubbles: true,
          composed: true,
        }))
      }
      this.fileName = ''
      this.fileInput.value = ''
    } catch (error) {
      this.error = error instanceof RmaError ? error.message : translations.error()
    } finally {
      this.uploading = false
    }
  }

  override render() {
    return html`
      <cse-form-field class="rma-file-upload" data-test="rma-file-upload">
        <cse-label slot="label" ?optional=${!this.required}>
          ${translations.label()}
          ${this.required ? nothing : html`<span slot="optional">${translations.optional()}</span>`}
        </cse-label>
        <input type="file" hidden @change="${() => this.onFileChange()}" />
        <cse-button-input
          button-variant="secondary"
          placeholder="${this.uploading ? translations.uploading() : this.fileName || translations.noFile()}"
          ?disabled="${this.isDisabled}"
          @cse-button-input-submit="${() => this.openPicker()}"
        >
          <span slot="button-text">${translations.choose()}</span>
        </cse-button-input>
        ${this.error
          ? html`<cse-text role="alert" class="rma-file-upload__error">${this.error}</cse-text>`
          : nothing}
      </cse-form-field>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-file-upload': CSCRmaFileUpload
  }
}
